import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Paper,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EditLocationAltOutlinedIcon from "@mui/icons-material/EditLocationAltOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import AppDialog from "../General/Dialog/AppDialog";

import TemplateEditor from "../General/TemplateEditor"; 

function SummaryPill({ label, value, color = "default", icon }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const styles = {
    default: {
      bg: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.62)",
      color: "text.primary",
      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
    },
    primary: {
      bg: "rgba(25,118,210,0.10)",
      color: "primary.main",
      border: "1px solid rgba(25,118,210,0.18)",
    },
    success: {
      bg: "rgba(46,125,50,0.10)",
      color: "success.main",
      border: "1px solid rgba(46,125,50,0.18)",
    },
  };

  const current = styles[color] || styles.default;

  return (
    <Box
      sx={{
        px: 1.4,
        py: 1,
        borderRadius: 2.5,
        display: "flex",
        alignItems: "center",
        gap: 1,
        background: current.bg,
        color: current.color,
        border: current.border,
      }}
    >
      <Box sx={{ display: "grid", placeItems: "center" }}>{icon}</Box>
      <Box>
        <Typography
          variant="caption"
          sx={{ opacity: 0.75, display: "block", lineHeight: 1 }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 900, fontSize: 14, lineHeight: 1.15 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function AddressCard({ address, onUse, selected = false }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.6,
        borderRadius: 3,
        height: "100%",
        background: selected
          ? "rgba(25,118,210,0.10)"
          : isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.62)",
        border: selected
          ? "1px solid rgba(25,118,210,0.24)"
          : isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
        boxShadow: "none",
        transition: "all .2s ease",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Chip
            size="small"
            icon={<HomeOutlinedIcon sx={{ fontSize: 16 }} />}
            label={address.type || "Sin tipo"}
            sx={{
              borderRadius: 2,
              fontWeight: 800,
              bgcolor: isDark ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.75)",
            }}
          />

          {selected && (
            <Chip
              size="small"
              icon={<TaskAltOutlinedIcon sx={{ fontSize: 16 }} />}
              label="Seleccionado"
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                bgcolor: "rgba(46,125,50,0.12)",
                color: "success.main",
              }}
            />
          )}
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 850, lineHeight: 1.25 }}>
            {address.address || "-"}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            {address.city || "Localidad no informada"}
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ opacity: 0.72 }}>
          Desde: {address.from_date || "-"}
        </Typography>
      </Box>

      <Button
        size="small"
        variant={selected ? "contained" : "outlined"}
        onClick={onUse}
        sx={{
          mt: 1.5,
          borderRadius: 2.2,
          fontWeight: 900,
          textTransform: "none",
        }}
      >
        {selected ? "Domicilio en uso" : "Usar este domicilio"}
      </Button>
    </Paper>
  );
}

function SectionBlock({ icon, title, description, children }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 3,
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.55)",
        border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.2,
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(25,118,210,0.10)",
            color: "primary.main",
            border: "1px solid rgba(25,118,210,0.16)",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 900, fontSize: 15.5, lineHeight: 1.15 }}
          >
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.25 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {children}
    </Paper>
  );
}

function buildDefaultTemplateHtml() {
  return `
    <blockquote><strong>WHATSAPP:</strong> {{lawyer_phone}} <br><strong>Ref:</strong> {{lawsuit_code}}</blockquote>
    <h1 style="text-align: center;">MUNICIPALIDAD</h1>
    <h1 style="text-align: center;">{{lawyer_document}}</h1>
    <h2 style="text-align: center">CÉDULA DE NOTIFICACIÓN</h2>

    <p><strong>TRIBUNAL:</strong> {{dependencies}} <br></p>
    <p><strong>SECRETARÍA:</strong> ÚNICA</p>
    <p><strong>DOMICILIO TRIBUNAL:</strong> ARTURO M. BAS 244</p>
    <p><strong>SEÑOR/A:</strong> {{party_name}}</p>
    <p><strong>DOMICILIO:</strong> {{party_address}}</p>

    <p style="text-align: justify"><strong>Se hace saber a Ud. que se ha dictado la siguiente resolución en el </strong> {{operation_text}}. Se acompaña copia de documental en ... fjs</p>

    <h4 style="text-align: center"><strong>{{lawyer_full_name}}</strong><br><strong>ABOGADO</strong><br><strong>MP: {{lawyer_document}}</strong></h4>

  `;
}

export default function NotificationCertificateAddressPreviewModal({
  open,
  onClose,
  items,
  setItems,
  onConfirm,
  loading,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [activeStep, setActiveStep] = useState(0);
  const [templateHtml, setTemplateHtml] = useState("");

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setTemplateHtml(buildDefaultTemplateHtml(items));
    }
  }, [open, items]);

  const handleAddressChange = (index, field, value) => {
    const updated = [...items];
    updated[index] = {
      ...updated[index],
      selected_address: {
        ...updated[index].selected_address,
        [field]: value,
        is_manual: true,
      },
    };

    setItems(updated);
  };

  const handleUseJusticeAddress = (index, address) => {
    const updated = [...items];
    updated[index] = {
      ...updated[index],
      selected_address: {
        type: address.type || "",
        address: address.address || "",
        city: address.city || "",
        from_date: address.from_date || "",
        is_manual: false,
      },
    };

    setItems(updated);
  };

  const total = items?.length || 0;
  const withJusticeAddresses =
    items?.filter((item) => item.justice_addresses?.length > 0).length || 0;

  const manualCount =
    items?.filter((item) => item.selected_address?.is_manual).length || 0;

  const handleNext = () => {
    if (activeStep === 0) {
      setTemplateHtml(buildDefaultTemplateHtml(items));
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleRestoreTemplate = () => {
    setTemplateHtml(buildDefaultTemplateHtml(items));
  };

  const handleGenerate = () => {
    onConfirm({
      items,
      templateHtml,
    });
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Previsualización de cédulas"
      subtitle="Primero revisá domicilios y después ajustá la plantilla antes de generar."
      maxWidth="lg"
      fullScreenMobile
      stickyHeader
      contentSx={{
        p: { xs: 1.2, sm: 2 },
        background: "transparent",
      }}
      actions={
        <>
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              borderRadius: 2.5,
              fontWeight: 800,
              textTransform: "none",
              px: 2.2,
            }}
          >
            Cancelar
          </Button>

          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              disabled={loading}
              sx={{
                borderRadius: 2.5,
                fontWeight: 800,
                textTransform: "none",
              }}
            >
              Volver
            </Button>
          )}

          {activeStep < 1 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={loading}
              sx={{
                borderRadius: 2.5,
                fontWeight: 900,
                textTransform: "none",
                px: 2.8,
              }}
            >
              Siguiente
            </Button>
          ) : (
            <Button
              onClick={handleGenerate}
              variant="contained"
              disabled={loading}
              sx={{
                borderRadius: 2.5,
                fontWeight: 900,
                textTransform: "none",
                px: 2.8,
                minWidth: 180,
              }}
            >
              {loading ? "Generando..." : "Guardar y generar"}
            </Button>
          )}
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 3,
            background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.55)",
            border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Stepper activeStep={activeStep} alternativeLabel>
            <Step>
              <StepLabel>Revisión de domicilios</StepLabel>
            </Step>
            <Step>
              <StepLabel>Plantilla editable</StepLabel>
            </Step>
          </Stepper>
        </Paper>

        {activeStep === 0 && (
          <>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 3,
                background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.55)",
                border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 1,
                }}
              >
                <SummaryPill
                  label="Registros"
                  value={total}
                  color="primary"
                  icon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />}
                />
                <SummaryPill
                  label="Con domicilios de justicia"
                  value={withJusticeAddresses}
                  color="success"
                  icon={<GavelOutlinedIcon sx={{ fontSize: 18 }} />}
                />
                <SummaryPill
                  label="Editados manualmente"
                  value={manualCount}
                  icon={<EditLocationAltOutlinedIcon sx={{ fontSize: 18 }} />}
                />
              </Box>
            </Paper>

            {items?.map((item, index) => {
              const selectedAddress = item.selected_address || {};
              const partyName = [item.party_name, item.party_document]
                .filter(Boolean)
                .join(" - ");

              return (
                <Accordion
                  key={`${item.legal_file_code}-${item.operation_code}-${index}`}
                  disableGutters
                  sx={{
                    borderRadius: "18px !important",
                    overflow: "hidden",
                    background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.62)",
                    border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                    boxShadow: "none",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 2,
                      py: 1.2,
                      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.45)",
                    }}
                  >
                    <Box sx={{ width: "100%", minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          lineHeight: 1.2,
                          mb: 0.5,
                        }}
                      >
                        <AttachFileRoundedIcon
                          sx={{
                            fontSize: 18,
                            mr: 0.6,
                            verticalAlign: "middle",
                          }}
                        />
                        {`${item.legal_file_code}`}

                        {` - ${partyName}` ||
                          "- Demandado principal sin identificar - "}

                        {` - ${selectedAddress.address}` ||
                          "- Sin dirección seleccionada"}
                        {selectedAddress.city
                          ? ` - ${selectedAddress.city}`
                          : ""}
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <SectionBlock
                          icon={<EditLocationAltOutlinedIcon />}
                          title="Domicilio que se usará en la cédula"
                          description="Podés dejar el seleccionado o corregirlo manualmente si hace falta."
                        >
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <TextField
                                label="Tipo"
                                value={selectedAddress.type || ""}
                                onChange={(e) =>
                                  handleAddressChange(
                                    index,
                                    "type",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                              />
                            </Grid>

                            <Grid size={{ xs: 12, md: 8 }}>
                              <TextField
                                label="Dirección"
                                value={selectedAddress.address || ""}
                                onChange={(e) =>
                                  handleAddressChange(
                                    index,
                                    "address",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                              />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                              <TextField
                                label="Localidad"
                                value={selectedAddress.city || ""}
                                onChange={(e) =>
                                  handleAddressChange(
                                    index,
                                    "city",
                                    e.target.value,
                                  )
                                }
                                fullWidth
                              />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                              <TextField
                                label="Fecha Desde"
                                type="date"
                                value={selectedAddress.from_date || ""}
                                onChange={(e) =>
                                  handleAddressChange(
                                    index,
                                    "from_date",
                                    e.target.value,
                                  )
                                }
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                              />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                              <Alert
                                severity="info"
                                icon={<InfoOutlinedIcon />}
                                sx={{
                                  borderRadius: 2.5,
                                  mt: 0.5,
                                }}
                              >
                                Si modificás este bloque manualmente, ese
                                domicilio será el que se utilice al generar la
                                cédula.
                              </Alert>
                            </Grid>
                          </Grid>
                        </SectionBlock>
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 0.5 }} />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <SectionBlock
                          icon={<GavelOutlinedIcon />}
                          title="Domicilios provenientes de justicia"
                          description="Podés elegir uno de estos domicilios para completar automáticamente la cédula."
                        >
                          {item.justice_addresses?.length > 0 ? (
                            <Grid container spacing={1.5}>
                              {item.justice_addresses.map(
                                (address, addrIndex) => {
                                  const isSelected =
                                    selectedAddress.type ===
                                      (address.type || "") &&
                                    selectedAddress.address ===
                                      (address.address || "") &&
                                    selectedAddress.city ===
                                      (address.city || "") &&
                                    selectedAddress.from_date ===
                                      (address.from_date || "") &&
                                    !selectedAddress.is_manual;

                                  return (
                                    <Grid
                                      key={addrIndex}
                                      size={{ xs: 12, md: 6 }}
                                    >
                                      <AddressCard
                                        address={address}
                                        selected={isSelected}
                                        onUse={() =>
                                          handleUseJusticeAddress(
                                            index,
                                            address,
                                          )
                                        }
                                      />
                                    </Grid>
                                  );
                                },
                              )}
                            </Grid>
                          ) : (
                            <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                              No hay domicilios cargados desde justicia para
                              este demandado principal.
                            </Alert>
                          )}
                        </SectionBlock>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </>
        )}

        {activeStep === 1 && (
          <SectionBlock
            icon={<DescriptionOutlinedIcon />}
            title="Plantilla editable"
            description="Esta plantilla se genera por defecto, pero podés ajustarla antes de crear la cédula."
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.2 }}>
              <Button
                startIcon={<RefreshOutlinedIcon />}
                onClick={handleRestoreTemplate}
                variant="outlined"
                sx={{
                  borderRadius: 2.2,
                  fontWeight: 800,
                  textTransform: "none",
                }}
              >
                Restaurar plantilla
              </Button>
            </Box>

            <TemplateEditor value={templateHtml} onChange={setTemplateHtml} />

            <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2.5 }}>
              El contenido editado se enviará como HTML al backend para generar
              la primera página de la cédula.
            </Alert>
          </SectionBlock>
        )}
      </Box>
    </AppDialog>
  );
}